import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGlobalState } from '../providers/globalState/GlobalStateProvider';
import { useData } from '../providers/data/DataProvider';
import { getStepAssets } from '../utils/stepAssets';
import { preloadImage, preloadChunk, preloadLottie, preloadDuringIdle, preloadPaymentPage } from '../utils/preloader';

export function useStepPreloader(): void {
  const { currentStep, struggles } = useGlobalState();
  const { data } = useData();
  const location = useLocation();
  const lastPreloadedKey = useRef<string | null>(null);

  useEffect(() => {
    // Extract sub-step indexes from URL
    let currentGrammarIndex = 0;
    let currentLexIndex = 0;
    let currentStruggleId: number | null = null;

    const grammarMatch = location.pathname.match(/^\/grammarTest\/(\d+)$/);
    if (grammarMatch) currentGrammarIndex = parseInt(grammarMatch[1], 10);

    const lexMatch = location.pathname.match(/^\/lexTest\/(\d+)$/);
    if (lexMatch) currentLexIndex = parseInt(lexMatch[1], 10);

    const struggleMatch = location.pathname.match(/^\/struggleView\/(\d+)$/);
    if (struggleMatch) currentStruggleId = parseInt(struggleMatch[1], 10);

    const showLexTest = (() => {
      const stored = localStorage.getItem('lexTestIsNeeded');
      if (stored === null) return true;
      return Boolean(Number(stored));
    })();

    const grammarQuestionCount = data?.grammar_test?.questions?.length || 0;
    const lexQuestionCount = data?.lex_test?.questions?.length || 0;

    const dataSignature = data
      ? [
          data.languages?.length || 0,
          data.hows?.length || 0,
          data.whys?.length || 0,
          data.struggles?.length || 0,
          data.topics?.length || 0,
          grammarQuestionCount,
          lexQuestionCount,
        ].join('|')
      : 'no-data';

    const stepKey = [
      currentStep,
      location.pathname,
      currentGrammarIndex,
      currentLexIndex,
      currentStruggleId ?? 'none',
      showLexTest ? 'lex' : 'no-lex',
      struggles.join(','),
      dataSignature,
    ].join('|');

    if (stepKey === lastPreloadedKey.current) return;
    lastPreloadedKey.current = stepKey;

    const assets = getStepAssets(currentStep, {
      data,
      selectedStruggles: struggles,
      currentStruggleId,
      showLexTest,
      currentGrammarIndex,
      grammarQuestionCount,
      currentLexIndex,
      lexQuestionCount,
    });

    // Priority 0: Payment page warmup (critical for conversion)
    if (assets.preloadPayment) {
      preloadPaymentPage();
    }

    // Priority 1: Component chunks (enables rendering)
    for (const chunk of assets.chunks) {
      preloadChunk(chunk.key, chunk.factory);
    }

    // Priority 2: Immediate images (next step's visuals)
    for (const url of assets.immediateImages) {
      preloadImage(url);
    }

    // Priority 3: Lottie animations
    for (const name of assets.lotties) {
      preloadLottie(name);
    }

    // Priority 4: Idle-time images (N+2 steps ahead)
    if (assets.idleImages.length > 0) {
      preloadDuringIdle(
        assets.idleImages.map(url => () => preloadImage(url))
      );
    }
  }, [currentStep, location.pathname, data, struggles]);
}
